import {
  OwFFormFieldKey,
  OwFFormFieldType,
} from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/consts/index";
import {
  IOwFFormFieldSchema,
  OwFFormField,
} from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/OwFFormField";
import { useOwFForm } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/useOwFForm";
import { isNotPseudoKey } from "@faire/retailer-visitor-shared/components/OpenWithFaire/OpenWithFaireApplication/utils";
import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import { Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { useState } from "@faire/web/ui/hooks/useState";
import React, { FC, useEffect } from "react";

interface IProps {
  optional?: boolean;
  formFields: IOwFFormFieldSchema[];
  formState: ReturnType<typeof useOwFForm>;
}

export const OwFFormSectionFields: FC<IProps> = ({
  optional,
  formFields,
  formState,
}) => {
  const {
    errors,
    formData,
    setValue,
    setMoneyRangeValue,
    eventPathBase,
    shouldAutoScroll,
    firstFailureKey,
    setShouldAutoScroll,
  } = formState;

  const [scrollRefs, setScrollRefs] = useState<{
    [key: string]: React.RefObject<HTMLDivElement | null>;
  }>({});

  // Create Scroll Refs
  useEffect(() => {
    const newRefs = formFields.reduce(
      (refs, field) => {
        refs[field.fieldName] = React.createRef();
        return refs;
      },
      {} as {
        [K in OwFFormFieldKey]?: React.RefObject<HTMLDivElement | null>;
      }
    );

    setScrollRefs(newRefs);
  }, [formFields]);

  // Auto-Scroll
  useEffect(() => {
    if (shouldAutoScroll && !optional && firstFailureKey) {
      const ref = scrollRefs[firstFailureKey]?.current;
      if (ref) {
        setShouldAutoScroll(false);
        getWindowOrThrow().scrollTo?.({
          top: ref.offsetTop - Math.min(ref.clientHeight, 200),
          behavior: "smooth",
        });
      }
    }
  }, [
    shouldAutoScroll,
    optional,
    firstFailureKey,
    scrollRefs,
    setShouldAutoScroll,
  ]);

  const renderFields = (fields: IOwFFormFieldSchema[]) =>
    fields.map((schema) => (
      <div
        key={schema.fieldName}
        ref={scrollRefs[schema.fieldName]}
        style={{
          width: schema.fullWidth ? "100%" : undefined,
        }}
      >
        <OwFFormField
          {...schema}
          onChange={
            schema.onChange ??
            ((newValue) => setValue(schema.fieldName, newValue))
          }
          onChangeRange={
            schema.fieldType === OwFFormFieldType.MONEY_RANGE_DROPDOWN
              ? (rangeIndexStr) =>
                  setMoneyRangeValue(
                    schema.fieldName,
                    schema.moneyRangeOptions,
                    rangeIndexStr
                  )
              : undefined
          }
          value={
            isNotPseudoKey(schema.fieldName)
              ? (formData[schema.fieldName] as string)
              : undefined
          }
          error={errors[schema.fieldName]}
          eventPathBase={eventPathBase}
          required={schema.required ?? false}
          isWholeSectionOptional={optional}
        />
        <Spacer height="1x" />
      </div>
    ));

  return (
    <>
      <Row gap={12} justify="space-between">
        {renderFields(formFields.filter((field) => field.isWrappedField))}
      </Row>
      {renderFields(formFields.filter((field) => !field.isWrappedField))}
      <Spacer height="1x" />
    </>
  );
};
